import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IShellEffects } from 'src/app/api/modules/core/components/effects/IShellEffects';
import { IShellFGEffectsConfetti } from 'src/app/api/modules/core/components/effects/foreground/IShellFGEffectConfetti';
import { IShellFGEffectsConfettis } from 'src/app/api/modules/core/components/effects/foreground/IShellFGEffectConfettis';

/**
 * The estimated durations of each effect in milliseconds.
 *
 * These are estimates because the true duration depends on the configuration.
 */
const ESTIMATED_CONFETTI_EFFECT_DURATION = 5000;
const ESTIMATED_CONFETTIS_EFFECT_DURATION = 5000;

/**
 * Dispatches server-configured shell effects to whoever renders them.
 *
 * Screens and plugins call {@link effect} with the effects portion of their
 * configuration; the shell's foreground effects container listens to the
 * fire-and-forget streams and renders one effect component per request.
 */
@Injectable({
  providedIn: 'root',
})
export class ShellEffectsService {
  /**
   * The confetti subject is the source of the confetti$ Observable.
   */
  private readonly confettiSubject = new Subject<IShellFGEffectsConfetti>();

  /**
   * The confetti Observable is fire and forget.
   */
  readonly confetti$ = this.confettiSubject.asObservable();

  /**
   * The confettis subject is the source of the confettis$ Observable.
   */
  private readonly confettisSubject = new Subject<IShellFGEffectsConfettis>();

  /**
   * The confettis Observable is fire and forget.
   */
  readonly confettis$ = this.confettisSubject.asObservable();

  /**
   * Dispatches all configured effects.
   *
   * @param config - The configuration for the effects.
   * @returns The estimated time in milliseconds until the last effect finishes
   * (the largest configured delay plus the largest effect duration). Callers
   * use this to postpone actions until the celebration has played out.
   */
  effect(config?: IShellEffects): number {
    if (!config?.fg) {
      return 0;
    }

    let estimatedEffectDelay = 0;
    let estimatedEffectDuration = 0;

    for (const target of config.fg) {
      // find the largest delay
      if (typeof target.delayMS === 'number' && target.delayMS > estimatedEffectDelay) {
        estimatedEffectDelay = target.delayMS;
      }

      switch (target.effectName) {
        case 'confetti':
          estimatedEffectDuration = Math.max(estimatedEffectDuration, ESTIMATED_CONFETTI_EFFECT_DURATION);
          this.confettiSubject.next(target);
          break;
        case 'confettis':
          estimatedEffectDuration = Math.max(estimatedEffectDuration, ESTIMATED_CONFETTIS_EFFECT_DURATION);
          this.confettisSubject.next(target);
          break;
      }
    }

    return estimatedEffectDuration + estimatedEffectDelay;
  }
}
